import React, { useEffect, useState } from 'react';
import {
  ClickAwayListener,
  Collapse,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

const Dock = styled('div')(({ theme }) => ({
  // Sticky under the page header so the trigger stays reachable while
  // scrolling long checklists, without eating a permanent left column.
  position: 'sticky',
  top: theme.spacing(1),
  zIndex: theme.zIndex.modal - 1,
  display: 'flex',
  justifyContent: 'center',
  pointerEvents: 'none',
  marginBottom: theme.spacing(1),
}));

const DockInner = styled('div')({
  pointerEvents: 'auto',
  width: 'min(480px, calc(100vw - 32px))',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
});

const Trigger = styled('button')(({ theme }) => ({
  appearance: 'none',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  // Always opaque — this dock floats over the checklist table. Using
  // theme.palette.action.hover alone replaces paper with a translucent
  // rgba and the table bleeds through on mouseover.
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  width: '100%',
  textAlign: 'left',
  font: 'inherit',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const TriggerText = styled('div')({
  minWidth: 0,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

const TriggerLabel = styled(Typography)({
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  opacity: 0.7,
  lineHeight: 1.2,
});

const TriggerValue = styled(Typography)({
  fontSize: '0.9rem',
  fontWeight: 600,
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const Chevron = styled(ExpandMoreIcon, {
  shouldForwardProp: prop => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  flexShrink: 0,
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  transform: open ? 'rotate(180deg)' : 'none',
}));

const Panel = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(1),
  maxHeight: 'min(70vh, 720px)',
  overflow: 'auto',
  boxShadow: theme.shadows[8],
  border: `1px solid ${theme.palette.divider}`,
  // Explicit solid fill — Paper defaults can be semi-transparent under
  // elevation overlays; keep the checklist from showing through.
  backgroundColor: theme.palette.background.paper,
  backgroundImage: 'none',
}));

const PanelHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 1, 0.5, 2),
  position: 'sticky',
  top: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const PanelBody = styled('div')(({ theme }) => ({
  padding: theme.spacing(1, 1, 1, 1),
  backgroundColor: theme.palette.background.paper,
  // Nested Section cards already carry their own margin; keep the panel tight.
  '& > *:last-child': {
    marginBottom: 0,
  },
}));

interface Props {
  selectedRelease: string | null;
  children: React.ReactNode;
  // When the selected release changes, collapse the panel so the user
  // lands back on the full-width table immediately.
  collapseOnReleaseChange?: boolean;
}

/**
 * Centered floating control that hosts the Manage releases / filters /
 * templates UI as a slide-down overlay. Used when the viewport is too
 * narrow to spare a permanent left column for that chrome.
 */
export const FloatingReleasePanel = ({
  selectedRelease,
  children,
  collapseOnReleaseChange = true,
}: Props) => {
  // Start open when nothing is selected so first-time visitors see the picker
  // without an extra click; collapse after a release is chosen.
  const [open, setOpen] = useState(() => !selectedRelease);

  useEffect(() => {
    if (!collapseOnReleaseChange) return;
    if (selectedRelease) {
      setOpen(false);
    }
  }, [selectedRelease, collapseOnReleaseChange]);

  // Select / Autocomplete menus portal to document.body, so the click lands
  // "outside" DockInner. Ignore those targets or the panel unmounts (and
  // kills the Select) before onChange can fire.
  //
  // Backstage's createUnifiedTheme prefixes MUI v5 classes with `v5-`, so we
  // match with [class*="Mui…"] rather than bare .MuiPopover-root.
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const el = event.target;
    if (!(el instanceof Element)) return;
    if (
      el.closest(
        [
          '[class*="MuiModal-root"]',
          '[class*="MuiPopover-root"]',
          '[class*="MuiMenu-root"]',
          '[class*="MuiMenu-paper"]',
          '[class*="MuiAutocomplete-popper"]',
          '[role="listbox"]',
          '[role="option"]',
        ].join(', '),
      )
    ) {
      return;
    }
    // Any open MUI portal menu still mounted (click may hit backdrop / void)
    if (
      document.querySelector(
        '[class*="MuiModal-root"] [role="listbox"], [class*="MuiPopover-root"] [role="listbox"], [class*="MuiAutocomplete-popper"]',
      )
    ) {
      return;
    }
    setOpen(false);
  };

  return (
    <Dock>
      <ClickAwayListener onClickAway={handleClickAway}>
        <DockInner>
          <Trigger
            type="button"
            aria-expanded={open}
            aria-controls="floating-release-panel"
            onClick={() => setOpen(v => !v)}
          >
            <TriggerText>
              <TriggerLabel>Manage releases</TriggerLabel>
              <TriggerValue>
                {selectedRelease ?? 'Pick a release…'}
              </TriggerValue>
            </TriggerText>
            <Chevron open={open} fontSize="small" />
          </Trigger>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Panel id="floating-release-panel" elevation={8}>
              <PanelHeader>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Releases & filters
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Close manage releases"
                  onClick={() => setOpen(false)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </PanelHeader>
              <PanelBody>{children}</PanelBody>
            </Panel>
          </Collapse>
        </DockInner>
      </ClickAwayListener>
    </Dock>
  );
};
